import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RevenuePoint } from "../../../interface/dashboard.interface";
import DashCard from "./DashCard";
import { fmtMoney } from "./format";

const NGN_COLOR = "#7600BC";
const USD_COLOR = "#F3A838";

const RevenueTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-hcBorder bg-white px-3 py-2 text-xs shadow-pop">
      <p className="mb-1 font-semibold text-honeConnectBlack">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-honeConnectGray">
          <span className="h-2 w-2 rounded-full" style={{ background: p.fill }} />
          {p.name}
          <span className="ml-auto pl-3 font-semibold text-honeConnectBlack">
            {fmtMoney(p.dataKey === "usd" ? "USD" : "NGN", Number(p.value))}
          </span>
        </p>
      ))}
    </div>
  );
};

/** NGN and USD collected, each on its own scale (left ₦, right $) */
const RevenueChart = ({ data, className }: { data?: RevenuePoint[]; className?: string }) => {
  const hasNgn = !!data?.some((d) => d.ngn !== undefined && d.ngn !== null);
  const hasUsd = !!data?.some((d) => d.usd !== undefined && d.usd !== null);
  const sum = (k: "ngn" | "usd") => data?.reduce((s, d) => s + (Number(d[k]) || 0), 0) ?? 0;

  const subtitle = [
    hasNgn ? `${fmtMoney("NGN", sum("ngn"), true)} in NGN` : null,
    hasUsd ? `${fmtMoney("USD", sum("usd"), true)} in USD` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <DashCard
      title="Revenue collected"
      subtitle={subtitle || undefined}
      empty={!hasNgn && !hasUsd}
      emptyNote="Needs revenue_series (NGN and USD collected) from the dashboard API."
      className={className}
      action={
        <div className="flex gap-3 text-xs text-hcMuted">
          {hasNgn && (
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: NGN_COLOR }} />
              NGN (left)
            </span>
          )}
          {hasUsd && (
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: USD_COLOR }} />
              USD (right)
            </span>
          )}
        </div>
      }
    >
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 0, left: -4, bottom: 0 }} barGap={3}>
            <CartesianGrid vertical={false} stroke="#EEF0F3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#8A8F9C" }} />
            <YAxis
              yAxisId="ngn"
              hide={!hasNgn}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: "#8A8F9C" }}
              tickFormatter={(v) => fmtMoney("NGN", v, true)}
              width={52}
            />
            <YAxis
              yAxisId="usd"
              orientation="right"
              hide={!hasUsd}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: "#8A8F9C" }}
              tickFormatter={(v) => fmtMoney("USD", v, true)}
              width={48}
            />
            <Tooltip content={<RevenueTooltip />} cursor={{ fill: "#F8F9FB" }} />
            {hasNgn && (
              <Bar yAxisId="ngn" dataKey="ngn" name="NGN" fill={NGN_COLOR} radius={[5, 5, 0, 0]} maxBarSize={22} />
            )}
            {hasUsd && (
              <Bar yAxisId="usd" dataKey="usd" name="USD" fill={USD_COLOR} radius={[5, 5, 0, 0]} maxBarSize={22} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashCard>
  );
};

export default RevenueChart;
